import * as React from 'react';

import { Box, Button, CircularProgress, Grid, List, Skeleton, Typography } from '@mui/material';
import { DataAPIContext, GET_LIST, GET_ONE } from '../../api';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { LinearProgress } from '@mui/material';
import clsx from 'clsx';
import { debounce } from "../../utils";
import { isScriptedInputOutsideEmbed } from "../../chromeEmbed";
import { makeStyles } from '@mui/styles';
import _ from 'lodash';
import { cv } from '../../themeVars';

// True for a ConversationsList that owns its own column rather than being embedded
// under ChatList's channel results — the single panel on mobile, or the middle panel
// on desktop. Those two keep their normal page size and height while searching; the
// embedded one shrinks to stay out of the way of the panels around it.
const isStandaloneConversationsList = (props) =>
    !props.isEmbeddedInChatList &&
    props.parent === "ConversationsList" &&
    typeof props.resource === "string" &&
    props.resource.includes("conversations");

const GridSkeleton = (props) => {
    const animation = props.animation || "wave";
    return <div>
        <Typography variant="h4"><Skeleton variant="text" animation={animation}/></Typography>
        <Grid container spacing={1}>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={6}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={6}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={3}><Skeleton variant="rect" animation={animation}/></Grid>
            <Grid item xs={12} sm={6}><Skeleton variant="rect" animation={animation}/></Grid>
        </Grid>
        </div>;
}
const useStyles = makeStyles(theme => ({
    sortButton: {
        minWidth: theme.spacing(4),
        minHeight: theme.spacing(4),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        padding: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    itemListRoot: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(12),
        overflowY: 'scroll',
        flexGrow: 1,
        
    },
    itemListRootNoMarginBottom: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(4),
        overflowY: 'scroll',
        flexGrow: 1,
    },
    withBorder: {
        border: `1px solid ${cv('border', '#d7d7d7')}`,
        borderRadius: '15px',
    },
    mobileWrapper: {
        display: 'flex',
        flexDirection: "column",
        height: '100%',
        width: "100%"
    },
    paginationButtons: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: "1.5rem"
    },
    nonSelectedRow: {
        cursor: "pointer",
        '&:not(:hover)': {
            background: "transparent !important",
          }
    },
    announcementsRow: {
        boxShadow: "0px 3px 3px rgba(0, 0, 0, 0.07)",
        marginBottom: "3px",
    },
    selectedRow: {
        cursor: "pointer",
        backgroundColor: `${cv('accent-tint', 'rgba(0, 122, 255, 0.08)')} !important`
    },
}));

const RecordList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [resource, setResource] = React.useState(props.resource);
    const [extraGetParams, setExtraGetParams] = React.useState(props.extra_get_params);
    const classes = useStyles();
    const disableSelectionOnClick = props.disableSelectionOnClick === void(0) ? true : props.disableSelectionOnClick;
    const [total, setTotal] = React.useState(null);
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(props.pageNum || 1);
    const [perPage, setPerPage] = React.useState(props.perPage || (props.isRecentActivityView ? 15 : 25));
    const defaultSortModel = props.sortModel || [{ field: 'id', sort: 'asc' }];
    const [sortModel, setSortModel] = React.useState(defaultSortModel);
    const [loading, setLoading] = React.useState(false);
    const [filterList, setFilterList] = React.useState([]);
    const [selectionModel, setSelectionModel] = React.useState([]);
    const rowsPerPageOptions = props.rowsPerPageOptions || [10, 15, 25, 50, 100];
    const totPages = Math.floor(total / perPage) + (total % perPage ? 1 : 0);
    const [searchText, setSearchText] = React.useState(props.initialSearchText || "");
    //react-usestateref lib allows accessing the latest state (avoids accessing stale state)
    const intervalNumRef = React.useRef(1); 
    const [asyncCountHandle, setAsyncCountHandle] = React.useState(null);
    //const lastUpdate = React.useRef();
    const timer = React.useRef(null);
    const [refreshDeps, setRefreshDeps] = React.useState(null);
    const refreshDepsRef = React.useRef(null);
    const backoffFor502errorMS = React.useRef(500);
    const extraGetParamsUpdateTimerRef = React.useRef(null);
    const pendingExtraGetParamsRef = React.useRef(null);
    // tour_operator.chat_search_past_tour_starts_within_x_days is a SerializerMethodField
    // resolved through get_config_value, so it already carries the operator override or the
    // platform default — read it, not tour_operator.config (which is unset for ~2/3 of
    // operators and would pin the fallback to a copy of the default that can drift).
    const chatSearchPastTourStartsWithinXDays =
        JSON.parse(window.localStorage.getItem('bootstrap') || '{}')?.tour_operator?.chat_search_past_tour_starts_within_x_days ?? 7;
    // How far back the last response looked, when it applied the recent-activity window.
    // Null whenever the window did not apply (a search or any filter switches it off), so
    // the empty state can only blame the window when the window is genuinely the reason.
    const [recencyWindowHours, setRecencyWindowHours] = React.useState(null);

    //const orderingField = props.orderingField || 'sequence';
    const isReorderingAllowed = false; //filterList.length === 0 && sortModel.length === 1 && sortModel[0].field === orderingField;
    //console.log('isReorderingAllowed', orderingField, filterList.length === 0, sortModel.length === 1 && sortModel[0].field === orderingField, '->', isReorderingAllowed);

    // Both the "no rows" and "no results" overlays say the same thing; they used to be
    // two copies of this markup and had already drifted apart in wording.
    const emptyStateBoxStyle = {
        fontSize: ".9rem",
        color: cv('text-secondary', "#747475"),
        textAlign: "center",
        paddingLeft: "15px",
        paddingRight: "15px",
        // The message is vertically centered, so in a grid whose rows area is only a
        // few px tall (e.g. the Activity view's collapsed-to-toolbar channel grid) the
        // glyphs would otherwise paint outside the box as a clipped strip of text.
        overflow: "hidden",
    };

    const renderEmptyStateText = () => {
        if (!props.resource) {
            return <span></span>;
        }
        if (props.resource.includes("conversations")) {
            if (searchText) {
                // Conversation search is NOT limited to a recency window — the Main Office
                // "recently active" gate now yields to an explicit query, and tour channels
                // never had one. The old copy here blamed a 7-day archive window, which sent
                // people hunting for a way to unarchive a conversation that was never hidden;
                // an empty result means the term genuinely matches nothing in this channel.
                return (
                    <span>
                        <br />
                        No conversations in this channel match &ldquo;{searchText}&rdquo;.
                    </span>
                );
            }
            // The Activity feed only looks back a fixed window, so an empty list here
            // usually means "nothing recent", not "nothing at all" — say which, and
            // point at the tab that shows everything. recencyWindowHours is only set
            // when the backend actually applied that window.
            if (recencyWindowHours) {
                return (
                    <span>
                        <br />
                        No activity in the last {recencyWindowHours} hours.
                        <br />
                        New messages appear here as they arrive — switch to All channels to browse every conversation.
                    </span>
                );
            }
            return (
                <span>
                    <br />
                    No conversations found.
                </span>
            );
        }
        if (props.resource.includes("templated_chat_messages")) {
            return (
                <span>
                    <br />
                    No results found
                </span>
            );
        }
        if (searchText) {
            return (
                <span>
                    <br />
                    No tours found within the past {chatSearchPastTourStartsWithinXDays} days. Tours are archived {chatSearchPastTourStartsWithinXDays} days after the conclusion of the tour, but can be unarchived via My Trips &gt; Chat Channels feature.
                </span>
            );
        }
        return (
            <span>
                <br />
                No tours found.
            </span>
        );
    };

    const EmptyStateOverlay = () => {
        // In the Activity view this (non-conversations) grid is collapsed to just its
        // search toolbar — rows render in the embedded ConversationsList below instead.
        // MUI still mounts the no-rows overlay when a search matches 0 channels, and
        // its text bled out of the ~1px-tall rows area as a strip of clipped glyphs
        // above the CONVERSATIONS label. The message would be misleading there anyway:
        // the real results (and their empty state) live in the list below.
        if (props.isRecentActivityView && props.resource && !props.resource.includes("conversations")) {
            return null;
        }
        return (
            <Box
                display="flex"
                height="100%"
                alignItems="center"
                justifyContent="center"
                style={emptyStateBoxStyle}
            >
                {renderEmptyStateText()}
            </Box>
        );
    };

    const components = {
        LoadingOverlay: LinearProgress,
        ...(props.hideToolbar ? {} : { Toolbar: GridToolbar }),
        NoRowsOverlay: EmptyStateOverlay,
        NoResultsOverlay: EmptyStateOverlay,
    };
    
    const onSelectionModelChange = (selection) => {
        setSelectionModel(selection.selectionModel);
        props.onSelectionModelChange && props.onSelectionModelChange(selection.selectionModel, selection);
    }
    const onRowClick = (params, ev) => {
        props.onRowClick && props.onRowClick(params, ev, {
            setLoading: setLoading
        });
    }
    const onPageChange = (newPage) => {
        setPage(newPage + 1);
        props.setPageNum?.(newPage + 1);
    }
    const onPageSizeChange = (newSize) => {
        console.debug('RecordList: pageSizeChange', perPage, newSize);
        setPerPage(newSize);
        props.setPerPage?.(newSize);
    }
    const onSortModelChange = (params) => {
        console.debug('RecordList: sortModelChange', params.sortModel);
        if (params.sortModel !== sortModel) {
            setSortModel(params.sortModel);
        }
    }
    const convertFilters = (flist) => {
        return (flist || []).map((f) => {
            try {
                const coldef = props.columns.filter((e) => (e.field === f.columnField))[0];
                console.log('RecordList: convertFilters: column=', f.columnField, ', coldef=', coldef);
                return {
                    field: f.columnField,
                    op: f.operatorValue,
                    value: f.value
                }
            } catch (e) {
                console.warning('RecordList: convertFilters: filter=', f, ', exception=', e, ', columns=', props.columns);
                return null;
            }
        }).filter(e => !!e)
    };
    const onFilterChange = React.useCallback((params) => {
        if (params && params.filterModel && params.filterModel.items) {
            const fl = params.filterModel.items.filter(x => !!x.value);
            console.log('RecordList: set filters', fl);
            setFilterList(fl);
        } else {
            setFilterList([]);
        }
    });
    const _sortModel2sort = (sm) => {
        if (sm && (sm.length > 0)) {
            return { field: sm[0].field, order: sm[0].sort };
        } else {
            return { field: defaultSortModel[0].field, order: defaultSortModel[0].sort };
        }
    }
    const handleReorder = (p, idx, nextIdx, ev) => {
        ev.stopPropagation();
        const order = sortModel[0].sort; // either 'asc' or 'desc'
        console.debug('RecordList: handleReorder', idx, nextIdx, sortModel[0].field, sortModel[0].sort);
        const nextId = p.api.getRowIdFromRowIndex(nextIdx);
        const nextRow = p.api.getRowFromId(nextId);
        const [a, b] = [nextRow.sequence, p.row.sequence];
        const rowOrderMap = {
            [p.row.id]: a,
            [nextRow.id]: b,
        };
        const newRows = rows.map(r => {
            if (rowOrderMap[r.id]) {
                return {...r, sequence: rowOrderMap[r.id] }
            } else {
                return r;
            }
        }).sort((a,b) => (order === "desc" ? b.sequence - a.sequence : a.sequence - b.sequence));
        setRows(newRows);
        props.onRowOrderUpdate && props.onRowOrderUpdate(rowOrderMap);
    }


    const reorderType = {
        type: 'number',
        width: 150,
        renderCell: (p) => {
            // console.log('row',p, p.rowIndex === p.api.getRowsCount() - 1);
            const upDisabled = !isReorderingAllowed || p.rowIndex === 0;
            const downDisabled = !isReorderingAllowed || p.rowIndex === p.api.getRowsCount() - 1;
            const title = null; //isReorderingAllowed ? null : `To reorder rows remove filters and sort the table by the '${orderingField}' field`;
            return (<React.Fragment>
                <Button disabled={upDisabled} title={title} onClick={ev => handleReorder(p, p.rowIndex, p.rowIndex - 1, ev)} variant="outlined" size="small" className={classes.sortButton}><ArrowUpwardIcon color={upDisabled ? "inherit" : "primary"}/></Button>
                <Button disabled={downDisabled} title={title} onClick={ev => handleReorder(p, p.rowIndex, p.rowIndex + 1, ev)} variant="outlined" size="small" className={classes.sortButton}><ArrowDownwardIcon color={downDisabled ? "inherit" : "primary"}/></Button>
                {p.row.sequence}
            </React.Fragment>);
        }
        
    }

    const columns = props.columns
        // .filter(c => ((c.type !== 'reorder') || isReorderingAllowed))
        .map(c => {
            if (c.type === 'reorder') {
                return {...c, ...reorderType }
            } else {
                return c;
            }
        });

    React.useEffect(() => {
        console.log('RecordList: Setting page to ', props.pageNum);
        setPage(props.pageNum || 1);
    }, [props.query, props.resource])

    React.useEffect(() => {
        console.log("RecordList: Emptying rows.");
        setRows([]);
    }, [props.resource])

    // Below this the backend can't use its trigram indexes, so is_short_only_query()
    // strips the search entirely and answers with the UNFILTERED list — i.e. the rows
    // already on screen, for the price of a full re-query (1.3 GB for a 40K-conversation
    // account; the 2026-08-19/24/28 "de" / "n" / "ra" timeouts). Sending nothing is both
    // cheaper and identical in what the user ends up looking at.
    //
    // Mirrors _normalize_search_tokens: per-token first (one token >= 3 chars is
    // searchable on its own), then the phrase fallback — an all-short query whose raw
    // text is >= _MIN_PHRASE_FALLBACK_LEN is searched as ONE phrase rather than
    // discarded, so "NO TI" (matching a "NO TICKETS" chip mid-type) reaches the backend.
    // Keep both constants in step with apiv2/views/edit/chat.py.
    const MIN_SEARCH_LEN = 3;
    const MIN_PHRASE_SEARCH_LEN = 5;
    const hasSearchableToken = (trimmed) =>
        !!trimmed && (trimmed.split(/\s+/).some((token) => token.length >= MIN_SEARCH_LEN)
                      || trimmed.length >= MIN_PHRASE_SEARCH_LEN);

    // True while the box holds text that will not be sent. Drives the "Keep typing to
    // search" hint so a too-short search visibly says why nothing is happening.
    const [searchTooShort, setSearchTooShort] = React.useState(false);

    const handleSearchQueryChange = (event) => {
        debouncedSearchQueryChangeHandler.cancel();
        const nextValue = event.target.value || "";
        const trimmed = nextValue.trim();
        // Too-short input commits "" rather than nothing: backspacing a committed
        // search below the minimum must not strand the list filtered by a term the
        // box no longer shows. While typing forward the committed value is already
        // "", so this costs nothing.
        const effectiveValue = hasSearchableToken(trimmed) ? nextValue : "";
        if (effectiveValue.trim() !== (searchText?.trim() || "")){
            // this is a hack to fix the issue with the X on the search bar. It was formerly clearing
            // the text, but not resetting the search
            requestSearch(effectiveValue)
        }
    };

    const requestSearch = (searchValue) => {
        console.log("Setting search text: ", searchValue);
        setPage(1);
        setSearchText(searchValue);
        props.onSearchTextChange?.(searchValue);
    };
    
    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 400);

    // Runs on every keystroke (the debounced commit above lags 400ms, too slow for
    // feedback). The boolean only flips at the empty <-> short <-> searchable
    // transitions, so the setState almost always bails out without re-rendering.
    const searchQueryChangeHandler = (event) => {
        const trimmed = (event.target?.value || "").trim();
        setSearchTooShort(!!trimmed && !hasSearchableToken(trimmed));
        debouncedSearchQueryChangeHandler(event);
    };

    // Runs in the capture phase, before the quick filter's own onChange reads the value.
    // An input event no person produced — a script set the value and dispatched the event,
    // which is exactly how the Ventrata side-panel extension "types" an order ref into this
    // box — is honoured only inside that side panel's embed. In an ordinary tab the box is
    // put back to the committed search before anything downstream sees it: React's value
    // tracker then sees no change, so neither the grid's onChange nor ours fires. The
    // extension's own clear-guard sees the empty box and stops re-applying. Typing, paste,
    // autofill and the clear X are trusted events and never enter here.
    const rejectScriptedSearchInput = (event) => {
        if (!isScriptedInputOutsideEmbed(event)) return;
        const input = event.target;
        if (input && 'value' in input && input.value !== (searchText || "")) {
            input.value = searchText || "";
        }
    };

    // update refreshDeps only if changed (these are the dependencies for the refreshFromBackend function)
    React.useEffect(() => {
        if(!_.isEqual(refreshDepsRef.current, [resource, perPage, page, searchText, extraGetParams])){
            refreshDepsRef.current = [resource, perPage, page, searchText, extraGetParams];
            setRefreshDeps(refreshDepsRef.current);
            console.log("RecordList: refreshDeps set to ", refreshDepsRef.current);
        }
    }, [resource, perPage, page, searchText, extraGetParams]);


    // Poll after initial load so lists stay roughly current (events also update the UI).
    // With an active search query, poll less often — search requests are heavier and
    // results change less often than the default conversation list.
    // TODO: factor in other events and test (conversationAdded, etc.)
    const pollIntervalMs = searchText.trim() ? 60000 : 25000;
    React.useEffect(() => {
        if (timer.current !== null){
            //console.debug("RecordList: Clearing intervalA");
            clearInterval(timer.current);
            intervalNumRef.current = intervalNumRef.current + 1;
            //console.log("intervalNum in useEffect (A):", resource, intervalNumRef.current)
        }
        timer.current = setInterval(
        (async () => {
            //setLoading(true);
            //console.debug("RecordList: Interval polling refresh");
            // Skip polls while the tab is hidden. Parked background tabs were
            // re-running heavy search queries (multi-GB on the backend) every
            // 60s indefinitely — the 2026-08-11 statement-timeout alerts. The
            // visibilitychange effect below refreshes once on return, so the
            // user never sees data older than their absence.
            if (document.visibilityState === 'hidden'){
                return;
            }
            refreshFromBackend();

        }), pollIntervalMs);

        return () => {
            //console.debug("RecordList: Clearing intervalB");
            clearInterval(timer.current);
            intervalNumRef.current = intervalNumRef.current + 1;
            //console.log("intervalNum in useEffect (B):", resource, intervalNumRef.current)
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDeps, props.listRefreshKey, searchText]);

    // Companion to the hidden-tab poll skip above: catch up as soon as the tab
    // becomes visible again, instead of waiting up to a full poll interval.
    React.useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible'){
                //console.debug("RecordList: Tab visible again; refreshing");
                refreshFromBackend();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshDeps, props.listRefreshKey, searchText]);

    const refreshFromBackend = async (show_loading=false) => {
        const currentInterval = intervalNumRef.current;
        const currentRefreshDeps = refreshDepsRef.current;

        if(show_loading){
            setLoading(true);
        }
        console.log("RecordList: Refreshing from backend.", resource, " (intervalNum=", currentInterval, ")");
        let params = {
            convert: props.convert,
            pagination: { page, perPage },
            sort: _sortModel2sort(sortModel),
            filter: {
                query: searchText,
                filters: JSON.stringify([...convertFilters(filterList), ...(props.apiFilters || [])]),
            }
        }

        if (extraGetParams != null){
            for(var key in extraGetParams){
                if(!(key in params)){
                    params[key] = JSON.stringify(extraGetParams[key])
                }
            }
        }

        const res = resource ? await dataAPI(GET_LIST, resource, params) : null

        // if a request hasn't finished before a newer request finishes, cancel the older request's results
        // (usually refreshFromBackend takes longer and returns more results than search. If a search starts
        // after refreshFromBackend starts, and search's response comes in sooner than refreshFromBackend's
        // response, this allows us to cancel the rendering from refreshFromBackend's response)
        // Only cancel if the refreshDepsRef object is the same and the rows are not empty 
        if (currentInterval !== intervalNumRef.current && (rows?.length > 0 || !_.isEqual(refreshDepsRef.current, currentRefreshDeps))){
            console.debug("RecordList: Canceling old RefreshFromBackend request for " + resource + " (" + currentInterval + ", " + intervalNumRef.current + ")");
            return
        }

        if (!res || res?.statusCode !== 200){
            console.log('RecordList: Error fetching data', res, backoffFor502errorMS.current);
            if(res?.statusCode === 502){
                console.error('RecordList: status code 502; Refreshing from backend ' + resource + " " + currentInterval + " " + intervalNumRef.current);
                setTimeout(() => {
                    refreshFromBackend(true);
                  }, backoffFor502errorMS.current);
                // Exponential backoff
                backoffFor502errorMS.current = Math.min(backoffFor502errorMS.current * 1.5, 10000);
            }
            else{
                if (show_loading){
                    setLoading(false);
                }
            }    
            return;
        }

        // success, so reset the backoff timer
        backoffFor502errorMS.current = 500;
    
        //console.log("RecordList: Refreshing from backend. Setting rows");
        // Optional `transformRows` prop lets the caller reorder / massage rows before the DataGrid
        // sees them. Used by the chat list to put the parent's App Support channel first within
        // the SUPPORT section when a franchise has multiple App Support channels visible.
        setRows(props.transformRows ? props.transformRows(res.data) : res.data);
        setRecencyWindowHours(res.recency_window_hours ?? null);
        if (props.firstsInList){
            props.firstsInList.current = props.resetFirstsInList?.();
        }
        
        if (res.total < 0) {
            console.log('recordList: data fetched', res);
            const num_to_show_on_page = (res.data || []).length;
            const prev_pages_total = (page - 1) * perPage;
            if(num_to_show_on_page >= perPage){
                setTotal(prev_pages_total + num_to_show_on_page + 1); // set to something bigger than the current page size to show the paginator controls
                props.setTotal?.(prev_pages_total + num_to_show_on_page + 1);
            }
            else {
                setTotal(prev_pages_total + num_to_show_on_page);
                props.setTotal?.(prev_pages_total + num_to_show_on_page);
            }
            
            if (res.async_count_handle) {
                setAsyncCountHandle(res.async_count_handle);
            }
        } else {
            setTotal(res.total);
            props.setTotal?.(res.total)
        }

        console.debug("RecordList: Loading: ", loading);
        //setTotal(res.total);
        //props.setTotal?.(res.total)
        //if(show_loading || loading){
            setLoading(false);
        //}
        
        props.setTotalAndFirstRow?.(res.total, res.data.length > 0 ? res.data[0] : null);

        // CP-8136: the chat list response carries whether the operator has any
        // staff/guide-group tours, so the filter menu only renders its toggle
        // for operators that use the concept.
        if (res.has_staff_chat_groups !== undefined) {
            props.setHasStaffChatGroups?.(res.has_staff_chat_groups);
        }
    }

    React.useEffect( () => {
        setResource(props.resource);
    }, [props.resource]);

    React.useEffect( () => {
        props.onLoadingChange?.(loading);
    }, [loading]);

    React.useEffect( () => {
        // Only update state if the actual values changed (deep comparison)
        // This prevents unnecessary state updates when the object reference changes but values are the same
        if (!_.isEqual(extraGetParams, props.extra_get_params)) {
            // Store the pending update
            pendingExtraGetParamsRef.current = props.extra_get_params;
            
            // Clear any existing timer
            if (extraGetParamsUpdateTimerRef.current) {
                clearTimeout(extraGetParamsUpdateTimerRef.current);
            }
            
            // Defer the update to batch rapid successive prop changes (e.g., when switching views)
            // This allows both excluded_date_categories and order_channels_by to be updated together
            // Using queueMicrotask runs after the current synchronous code but before the next event loop tick
            // This prevents redundant backend calls when multiple props change in quick succession
            // Note: We use a ref to track the timer for cleanup, but queueMicrotask doesn't return a handle,
            // so we use setTimeout(0) which has equivalent behavior and allows proper cleanup
            extraGetParamsUpdateTimerRef.current = setTimeout(() => {
                const pendingParams = pendingExtraGetParamsRef.current;
                if (pendingParams) {
                    // Always apply the latest pending value (it may have been updated multiple times)
                    // The initial check ensures we only defer if the value actually changed
                    if(props.resource && !props.resource.includes("conversations")){
                        console.log('RecordList: Setting extraGetParams to ', pendingParams);
                    }
                    setExtraGetParams(pendingParams);
                    pendingExtraGetParamsRef.current = null;
                }
            }, 0); // Defer to next event loop tick to batch rapid updates
        }
        
        // Cleanup timer on unmount
        return () => {
            if (extraGetParamsUpdateTimerRef.current) {
                clearTimeout(extraGetParamsUpdateTimerRef.current);
            }
        };
    }, [props.extra_get_params, extraGetParams, props.resource]);

    React.useEffect(() => {
        //console.debug("RecordList: New resource: ", resource)
        if(props.refreshFromBackendFunc){
            props.refreshFromBackendFunc.current = refreshFromBackend
        }
        //const currentUpdate = resource + " " + searchText + " " + page + " " + perPage + " " + JSON.stringify(extraGetParams);
        if (resource && refreshDeps){
            (async () => {
                await refreshFromBackend(true);
            })();
        }
      }, [refreshDeps])

      React.useEffect(() => {
        const incomingSearchText = props.searchText?.trim() || "";
        // A changed search restarts from page 1: the page the user had paginated to
        // rarely exists in the newly filtered set, and fetching it returns an empty
        // grid whose overlay then misreads as "conversations archived". Guarded on an
        // actual change so the mount-time run (searchText initialises from the same
        // parent state) cannot clobber a page number restored from router state. The
        // quick-filter path resets separately in requestSearch, which also feeds this
        // prop, hence the guard doing double duty as a loop-breaker.
        if (incomingSearchText !== (searchText?.trim() || "")) {
            setPage(1);
            props.setPageNum?.(1);
        }
        setSearchText(incomingSearchText)
        // The 5-per-page search view exists for the list ChatList embeds beneath its
        // channel results, where the whole point is to stay small. A standalone
        // ConversationsList owns its column — one panel on mobile, the middle panel
        // on desktop (CP-8132) — so it keeps the normal page size while searching.
        if (props.searchText && !isStandaloneConversationsList(props)) {
            setPerPage(props.searchResultsPerPage || 5);
        } else {
            setPerPage(props.perPage || 25);
        }
      }, [props.searchText, props.perPage, props.show1pageScreen, props.isEmbeddedInChatList, props.parent, props.resource, props.searchResultsPerPage])

      React.useEffect(() => {
        if (!asyncCountHandle) { return; }
        let active = true;
        const refreshAsyncCount = () => {
            dataAPI(GET_ONE, resource, { urlSuffix: 'async_count/', filter: { handle: asyncCountHandle } }).then((res) => {
                if (!active) { return; }
                if (res.statusCode === 200) {
                    if (typeof res.data === 'number' && res.data >= 0 || typeof res.total === 'number' && res.total >= 0) {
                        console.log('recordList: got async count', res.data);
                        setTotal(typeof res.data === 'number' && res.data >= 0 ? res.data : res.total);
                        setAsyncCountHandle(null);
                        clearInterval(tm);
                    }
                } else {
                    console.log('got error', res);
                }
            });
        };
        const tm = setInterval(refreshAsyncCount, 1000);
        refreshAsyncCount();
        return () => {
            active = false;
            clearInterval(tm);
        };
    }, [dataAPI, resource, sortModel, filterList, props.query, props.deps, props.hiddenAPIFilters, props.apiFilters, asyncCountHandle]);
      
    const onEditCellChangeCommitted = (change, ev) => {
        props.onEditCellChangeCommitted && props.onEditCellChangeCommitted(change, ev);
    }

    //console.debug(`RecordList[${resource}]: total=${total} page=${page} loaded=${rows?.length} | sort=`, sortModel, 'loading=', (loading || props.relatedLoading));
    if (rows === null) {
        return <GridSkeleton/>
    } else {
        const filteredRows = rows.filter((row) => {
            return (props.deletedList || []).indexOf(row.id) < 0;
        })

        if (props.isSmallScreen && props.listItemRenderer) {
            const hasPreviousPage = page > 1;
            const hasNextPage = page * perPage < total;
            const gridApi = {
                onRowClick: onRowClick,
                columns: props.columns,
                rows: props.rows,
                filteredRows: filteredRows,
                props: props,
            }
            const goToPreviousPage = () => (hasPreviousPage && setPage(page - 1));
            const goToNextPage = () => (hasNextPage && setPage(page + 1));

            return <div className={clsx(classes.mobileWrapper)} >
                { loading ?
                <Box position="fixed" top="50%" left="50%" style={{ transform: "translate(-50%, -50%)" }}><CircularProgress color="primary"/></Box>
                    :
                    ( filteredRows.length > 0 ? 
                        <>
                            <Grid className={clsx(hasPreviousPage || hasNextPage ? classes.itemListRootNoMarginBottom : classes.itemListRoot, !props.disableListBorder && classes.withBorder,)} >
                                <Grid item xs={12}>
                                    <List>
                                        { filteredRows.map((row, idx) => props.listItemRenderer(row, idx, filteredRows, gridApi)) }
                                    </List>
                                </Grid>
                            </Grid>
                            {hasPreviousPage || hasNextPage ?
                                <div className={clsx(classes.paginationButtons)} >
                                    <Button disabled={!hasPreviousPage} variant="outlined" color="primary" onClick={goToPreviousPage}><ArrowBackIcon size="small" /></Button>
                                    <Typography variant="body1">Page {page} of {totPages}</Typography>
                                    <Button disabled={!hasNextPage} variant="outlined" color="primary" onClick={goToNextPage}><ArrowForwardIcon size="small" /></Button>
                                </div>
                                : null}
                        </>
                    : <Box display="flex" justify="center" >Nothing found</Box>
                    )
                }
            </div>;
        } else {
            let chatListRowTotal = props.chatListRowTotal;
            if (chatListRowTotal == null){
                chatListRowTotal = 6;
            }
            if (chatListRowTotal < 4){
                chatListRowTotal = 4;
            }
            // Mirror the Math.min(thisTotal, 6) cap used for the channel grid's height
            // below: both formulas share the same 5.5svh-per-row term, and they only
            // cancel out (keeping the left column's bottom border aligned with the
            // other panels) when both clamp the row count to the same [4, 6] range.
            if (chatListRowTotal > 6){
                chatListRowTotal = 6;
            }
            let thisTotal = total;
            if (thisTotal < 4){
                thisTotal = 4;
            }
            // Shrink the grid when searching so the embedded search UI (e.g. ChatList) leaves room for other panels.
            // A standalone ConversationsList owns its column, so keep height flexible (null) and let it fill.
            const shouldShrinkHeight =
                props.isRecentActivityView || (props.searchText && !isStandaloneConversationsList(props));

            let height = null;
            let collapsedToToolbar = false;

            if (props.autoHeight){
                // Grid sizes itself to its rows (autoHeight DataGrid prop below)
                height = null;
            }
            else if (props.heightOverride){
                height = props.heightOverride;
            }
            else{
            if (shouldShrinkHeight) {
                if (props.resource.includes("conversations")) {
                    if(props.isRecentActivityView){
                        height = null;
                    }
                    else{
                    // Fill exactly what remains of the left column's 86svh: the channel
                    // grid above is 11svh + 25px - .08vh + rows*5.5svh and the
                    // CONVERSATIONS label between the grids is 33.6px (12px padding +
                    // 1px border + one .9rem line at the default 16px root font), so
                    // remainder = 42svh - 58.6px + .08vh + (6 - rows)*5.5svh. The +.08vh
                    // mirrors the channel grid's -.08vh; without it the bottom border
                    // drifted up to ~1px off depending on viewport height.
                    height = `calc(42svh - 58.6px + .08vh + ${(6 - chatListRowTotal) * 5.5}svh)`;
                        }
                } else if (props.isRecentActivityView) {
                        // Collapsed to just the search toolbar; rows render in the embedded ConversationsList instead
                        height = `49px`;
                        collapsedToToolbar = true;
                } else {
                    height = `calc(11svh + 24px + 1px - .08vh + ${Math.min(thisTotal, 6) * 5.5}svh)`;
                    }
                }
            }
            
            const headerCollapsed = props.headerHeight === 0 || props.hideHeader;

            return <DataGrid
                sx={{
                    '&.MuiDataGrid-root': {
                        borderRadius: 0,
                        borderTop: `0px solid ${cv('border', 'rgb(224, 224, 224)')}`,
                        height: height,
                        borderBottom: props.hideBottomBorder ? `0px solid ${cv('border', 'rgb(224, 224, 224)')}` : null,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                        backgroundColor: cv('surface-muted', "#F5F5F5"),
                    },
                    '& .MuiDataGrid-selectedRowCount': {
                        display: "none",
                    },
                    '& .MuiTablePagination-root': {
                        position: "absolute",
                        right: 0,
                    },
                    '& .MuiToolbar-root': {
                        overflow: "hidden",
                        paddingLeft: 0,
                    },
                    '& .MuiTablePagination-actions': {
                        marginLeft: "5px",
                    },
                    '& .MuiInputBase-root': {
                        marginRight: "20px",
                    },
                    '& .MuiSvgIcon-root': {
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        ...(headerCollapsed
                            ? { borderTop: `1px solid ${cv('border', 'rgb(224, 224, 224)')}` }
                            : {}),
                        // The ~1px-tall scroller still holds all the rows, so the browser paints a stub scrollbar
                        ...(collapsedToToolbar ? { overflow: 'hidden' } : {}),
                    },
                    '& .MuiLinearProgress-root': {
                        // The toolbar-only grid has no visible rows area, so its loading bar renders squished;
                        // hideLoadingIndicator lets the parent show its own bar instead (e.g. above the CONVERSATIONS label)
                        display: (collapsedToToolbar || props.hideLoadingIndicator) ? 'none' : null,
                    },
                    '& .Mui-resizeTriggers':{
                    },
                    '& .MuiDataGrid-footerContainer':{
                        display: props.hideFooter ? "none" : null,
                        height: props.parent === "ChatList" && props.searchText ? '40px' : null,
                        minHeight: props.parent === "ChatList" && props.searchText ? '40px' : null,
                        boxShadow: props.parent === "ChatList" && props.searchText ? "0px 3px 3px rgba(0, 0, 0, 0.07)" : null
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        display: props.hideHeader ? 'none' : null,
                        borderBottom: headerCollapsed ? 'none !important' : null,
                    },
                    /*'& .MuiTablePagination-selectLabel': {
                    }*/
                }}
                headerHeight={props.headerHeight}
                className={props.className}
                pagination
                checkboxSelection={props.checkboxSelection !== void (0) ? props.checkboxSelection : true}
                autoPageSize={props.autoPageSize}
                filterMode="server"
                paginationMode="server"
                sortingMode={props.sortingMode || "server"}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                onFilterModelChange={onFilterChange}
                page={page - 1}
                pageSize={perPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                rowCount={total}
                columns={columns}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
                onRowClick={onRowClick}
                rowHeight={props.rowHeight}
                getRowHeight={() => 'auto'}
                autoHeight={props.autoHeight}
                disableSelectionOnClick={disableSelectionOnClick}
                density="compact"
                rows={rows}
                loading={loading || props.relatedLoading}
                components={components}
                onEditCellChangeCommitted={onEditCellChangeCommitted}
                getRowClassName={(params) => params.row.is_announcements_thread ?
                                                params.row.id === props.getSelectedRow ? 
                                                    `${classes.announcementsRow} ${classes.selectedRow}` : 
                                                `${classes.announcementsRow} ${classes.nonSelectedRow}` : 
                                             params.row.id === props.getSelectedRow ? 
                                                `${classes.selectedRow}` : 
                                            `${classes.nonSelectedRow}`}
                disableVirtualization
                //components={{ Toolbar: GridToolbar }}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                initialState={{
                    filter: {
                        filterModel: {
                            items: [],
                            quickFilterValues: [searchText]
                        }
                    }
                }}
                componentsProps={{
                    toolbar: {
                        csvOptions: { disableToolbarButton: true },
                        printOptions: { disableToolbarButton: true },
                        showQuickFilter: !(props.hideQuickFilter === true),
                        value: searchText,
                        // These reach the quick filter's input by event delegation:
                        // they sit on the GridToolbarContainer div and catch the
                        // bubbled change (keystrokes) and click (the clear X) events.
                        onChange: searchQueryChangeHandler,
                        onClick: searchQueryChangeHandler,
                        onInputCapture: rejectScriptedSearchInput,
                        quickFilterProps: {
                            placeholder: props.searchPlaceholder || "Search",
                            helperText: searchTooShort ? "Keep typing to search" : null,
                            //debounceMs: 500 
                        },
                        sx: {
                            '& .MuiBox-root': {
                                display: 'None',
                            },
                            // The too-short hint floats inside the right end of the
                            // box (clear of the always-mounted X) instead of below
                            // it, so showing it never changes the toolbar height.
                            '& .MuiFormHelperText-root': {
                                position: 'absolute',
                                right: '42px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                margin: 0,
                                pointerEvents: 'none',
                                whiteSpace: 'nowrap',
                                color: cv('text-muted', '#9e9e9e'),
                            },
                            '& .MuiFormControl-root': {
                                marginLeft: '5px',
                                marginTop: '5px',
                                marginBottom: '5px',
                                backgroundColor: cv('surface', "white"),
                                paddingLeft: props.show1pageScreen && props.parent !== "ChatList" ? "6px" : "8px",
                                paddingRight: props.show1pageScreen && props.parent !== "ChatList" ? "8px" : "0px",
                                marginRight: 0,
                                paddingTop: "0px",
                                paddingBottom: "2px",
                                borderRadius: "5px",
                                width: props.parent === "ChatList" ? "min(calc(100% - 8rem), min(800px, 72%))" : (props.show1pageScreen ? "calc(100% - 10.5rem)" : "calc(70% - 30px)")
                            },
                            '& .MuiInputBase-root': {
                                marginRight: props.show1pageScreen && props.parent !== "ChatList" ? 0 : "5px"
                            },
                            ...(props.show1pageScreen && props.parent !== "ChatList" ? {
                                '& .MuiInputBase-input': {
                                    fontSize: '0.875rem',
                                },
                            } : {}),
                            // GridToolbarQuickFilter keeps its clear X in-flow with
                            // visibility:hidden, which eats ~40px and clips the
                            // "booking" in the mobile conversations placeholder.
                            ...(props.show1pageScreen && props.parent !== "ChatList" ? {
                                '& .MuiInputBase-root:has(input:placeholder-shown) > .MuiIconButton-root': {
                                    display: 'none',
                                },
                            } : {}),
                        },
                    },
                    loadingOverlay: {
                        style: { zIndex: "4" }
                      }
                }}
            />

        }
    }

};

export default RecordList;
